import React, { useEffect, useRef, useState } from 'react';
import MultiSelect from 'components/elements/MultiSelect';
import Grid from 'components/elements/Grid';
import FieldSetWithTitle from 'shared/FieldSetWithTitle/components/FieldSetWithTitle';
import InputGroupComponent from 'components/groups/InputGroup';
import { usePriceContext } from 'contracts/utils/priceContext';
import {
  fetchPricingPreview,
  DdpCost,
  PricingBreakdown,
  SimulatorField,
  Premium,
  CatalogOption,
} from 'contracts/utils/fetchPricingPreview';
import { PriceBreakdownTooltip } from './PriceBreakdownTooltip';

// PLA-1057: sección del Pricing Simulator. Muestra el precio calculado (FOB + el del incoterm
// real) y lo compara con lo ingresado; si el ingresado queda por debajo, pide motivo + documento
// y el contrato irá a aprobación manual. Solo FOB/EXW/DDP/FCA/DAP.
//
// Los "Additional fields" se renderizan 100% dinámicos desde el catálogo del API:
//   - `fields`         (comunes a todo incoterm)  -> los visible:true (ej. premiumIds)
//   - `incotermFields` (por incoterm)             -> los visible:true (ej. DDP: carry/storage)
//   - `ddpCosts` con `editable: true`             -> inputs override (DDP/FCA/DAP), keyed por id
// Cada campo se manda al calculate por su `name`; los overrides por `ddp_cost_id`. Cero cost_type
// hardcodeado. El nombre del campo == la key del calculate (contrato del API).
interface Props {
  incoterm: string;
  qualityId: string;
  shipmentDate: Date;
  shipperId: string;
  opportunityId?: string;
  destinationWarehouseId: string;
  priceType?: string;
  qualityName?: string;
  pricingFacilityConfigured?: boolean;
  markIsPeebies?: boolean;
  // PLA-1111: el contrato no se puede crear si el simulador podría validarlo pero falta
  // configuración (o un valor es inválido). Cambia el mensaje: no va a aprobación manual.
  blocksOnMissingPriceData?: boolean;
}

type Status = 'idle' | 'loading' | 'ok' | 'unsupported';

const SUPPORTED_INCOTERMS = ['FOB', 'EXW', 'DDP', 'FCA', 'DAP'];
// FCA se comporta igual que DDP del lado del Price Simulator: exige bodega de destino y trae
// los ddpCosts variables de la combinación facility+bodega (PLA-1079).
// PLA-1071: DAP también.
const WAREHOUSE_INCOTERMS = ['DDP', 'FCA', 'DAP'];
const t = (key: string, params?: Record<string, unknown>): string =>
  I18n.translate(`contracts.price_simulator.${key}`, params);

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// camelCase (nombre del campo) -> snake_case (key que permite Rails; el backend la re-camelize).
const toSnake = (name: string): string => name.replace(/([A-Z])/g, '_$1').toLowerCase();

const localize = (label?: Record<string, string>, fallback = ''): string => {
  if (!label) return fallback;
  const locale = I18n.locale || 'en';
  return label[locale] ?? label.en ?? fallback;
};

export function PriceSimulator({
  incoterm,
  qualityId,
  shipmentDate,
  shipperId,
  opportunityId,
  destinationWarehouseId,
  priceType,
  qualityName,
  pricingFacilityConfigured = false,
  markIsPeebies = false,
  blocksOnMissingPriceData = false,
}: Props) {
  const {
    fobPriceUsdLb,
    priceUsdLb,
    units,
    setPriceBelowSimulator,
    setFobBelowSimulator,
  } = usePriceContext();

  // Catálogo (se conserva entre recálculos de precio; solo se sobreescribe cuando el backend lo
  // envía, es decir cuando NO se mandó skip_catalog).
  const [fields, setFields] = useState<SimulatorField[]>([]);
  const [incotermFields, setIncotermFields] = useState<SimulatorField[]>([]);
  const [premiums, setPremiums] = useState<Premium[]>([]);
  const [boxes, setBoxes] = useState<CatalogOption[]>([]);
  const [ddpCosts, setDdpCosts] = useState<DdpCost[]>([]);

  // Valores que ingresa el usuario. premiumIds aparte (array); el resto por nombre de campo.
  const [premiumIds, setPremiumIds] = useState<number[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [ddpOverrides, setDdpOverrides] = useState<Record<string, string>>({});

  const [fobCalc, setFobCalc] = useState<number | null>(null);
  const [incotermCalc, setIncotermCalc] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<PricingBreakdown | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [reason, setReason] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);

  const isSupportedIncoterm = SUPPORTED_INCOTERMS.includes(incoterm);
  const usesWarehouse = WAREHOUSE_INCOTERMS.includes(incoterm);
  const needsFacility = incoterm === 'EXW' || usesWarehouse;
  const facilityMissing = needsFacility && !pricingFacilityConfigured;
  const unitsValue = units?.value;
  const unitsMissing = !unitsValue || Number(unitsValue) <= 0;
  const warehouseMissing = usesWarehouse && !destinationWarehouseId;

  // Campos a pintar: fields + incotermFields visibles. Overrides: ddpCosts editables (DDP/FCA/DAP).
  const visibleFields = [...fields, ...incotermFields].filter((f) => f.visible);
  const editableCosts = usesWarehouse
    ? ddpCosts.filter((c) => c.editable ?? c.cost_type === 'VARIABLE_AT_QUOTE')
    : [];
  const hasAdditionalFields = visibleFields.length > 0 || editableCosts.length > 0;

  // ¿Se eligió una prima negativa (usdPerLb < 0)? -> exige marca PEEBIES.
  const negativePremiumSelected = premiumIds.some(
    (id) => (premiums.find((p) => p.id === id)?.usdPerLb ?? 0) < 0
  );
  const peebiesMissing = negativePremiumSelected && !markIsPeebies;

  const fieldMissing = (field: SimulatorField): boolean => {
    if (!field.required) return false;
    if (field.type === 'multiselect') return premiumIds.length === 0;
    const value = fieldValues[field.name];
    return value === undefined || value === '';
  };
  const additionalFieldsMissing =
    visibleFields.some(fieldMissing) ||
    editableCosts.some((c) => (ddpOverrides[c.ddp_cost_id] ?? '') === '');

  // PLA-1102: el API manda un mínimo por campo (`min`). El atributo `min` del input no frena
  // nada —el simulador consulta en cada cambio—, así que un valor por debajo llegaba al API y
  // volvía como error genérico. Se valida acá y no se consulta hasta corregirlo.
  const fieldBelowMin = (field: SimulatorField): boolean => {
    if (field.type !== 'number' || field.min == null) return false;
    const value = fieldValues[field.name];
    if (value === undefined || value === '') return false;
    return Number(value) < field.min;
  };
  const firstFieldBelowMin = visibleFields.find(fieldBelowMin);

  // Los costos DDP editables traen su propio `min` (el valor configurado).
  const costBelowMin = (cost: DdpCost): boolean => {
    if (cost.min == null) return false;
    const value = ddpOverrides[cost.ddp_cost_id];
    if (value === undefined || value === '') return false;
    return Number(value) < cost.min;
  };
  const firstCostBelowMin = editableCosts.find(costBelowMin);

  // Precarga defaults cuando llega el catálogo (number con `default`, y overrides con su `value`).
  // PLA-1102: sólo en los campos obligatorios. Custom Margin trae como `default` el EXW markup,
  // pero es opcional: queda vacío hasta que el usuario lo llene.
  useEffect(() => {
    setFieldValues((prev) => {
      let changed = false;
      const next = { ...prev };
      [...fields, ...incotermFields].forEach((f) => {
        if (f.required && f.default != null && next[f.name] === undefined) {
          next[f.name] = String(f.default);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [fields, incotermFields]);

  useEffect(() => {
    setDdpOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      editableCosts.forEach((cost) => {
        if (next[cost.ddp_cost_id] === undefined) {
          next[cost.ddp_cost_id] = String(cost.value);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line
  }, [ddpCosts]);

  // pricing_inputs para el request (snake_case; el backend re-camelize para el API).
  const buildPricingInputs = (): Record<string, unknown> => {
    const out: Record<string, unknown> = { premium_ids: premiumIds };
    visibleFields.forEach((f) => {
      if (f.type === 'multiselect') return;
      const value = fieldValues[f.name];
      if (value !== undefined && value !== '') out[toSnake(f.name)] = value;
    });
    return out;
  };

  const inputsKey = JSON.stringify({ premiumIds, fieldValues, ddpOverrides });
  const canQuery =
    isSupportedIncoterm &&
    !facilityMissing &&
    !unitsMissing &&
    !warehouseMissing &&
    !!qualityId &&
    !firstFieldBelowMin &&
    !firstCostBelowMin;

  // El catálogo depende de incoterm/origen/bodega/calidad/unidades -> sólo se re-pide cuando eso
  // cambia. PLA-1102: calidad y unidades entran en la clave porque el catálogo trae límites que
  // dependen de ellas (el mínimo de Custom Margin es el EXW markup de país/calidad/volumen/facility).
  const catalogKey = [
    incoterm,
    shipperId,
    destinationWarehouseId,
    qualityId,
    unitsValue,
  ].join('|');
  const lastCatalogKey = useRef<string | null>(null);

  useEffect(() => {
    if (!canQuery) {
      setStatus('idle');
      setFobCalc(null);
      setIncotermCalc(null);
      return undefined;
    }

    const needCatalog = lastCatalogKey.current !== catalogKey || fields.length === 0;

    const handler = setTimeout(async () => {
      setStatus('loading');
      try {
        const { data } = await fetchPricingPreview({
          incoterm,
          qualityId,
          units: unitsValue,
          priceType,
          shipmentDate: toIsoDate(shipmentDate),
          shipperId,
          opportunityId,
          destinationWarehouseId,
          pricingInputs: buildPricingInputs(),
          ddpOverrides,
          skipCatalog: !needCatalog,
        });

        if (data?.fields) setFields(data.fields);
        if (data?.incoterm_fields) setIncotermFields(data.incoterm_fields);
        if (data?.premiums) setPremiums(data.premiums);
        if (data?.boxes) setBoxes(data.boxes);
        if (needCatalog) setDdpCosts(data?.ddp_costs || []);
        lastCatalogKey.current = catalogKey;

        if (
          data &&
          data.supported &&
          data.fob_price != null &&
          data.incoterm_price != null
        ) {
          setFobCalc(Number(data.fob_price));
          setIncotermCalc(Number(data.incoterm_price));
          setBreakdown(data.breakdown ?? null);
          setReason(null);
          setApiMessage(null);
          setStatus('ok');
        } else {
          setFobCalc(null);
          setIncotermCalc(null);
          setBreakdown(null);
          setReason(data?.reason ?? null);
          setApiMessage(data?.error ?? null);
          setStatus('unsupported');
        }
      } catch {
        setBreakdown(null);
        setReason('calculation');
        setApiMessage(null);
        setStatus('unsupported');
      }
    }, 350);

    return (): void => clearTimeout(handler);
    // eslint-disable-next-line
  }, [
    canQuery,
    incoterm,
    qualityId,
    unitsValue,
    priceType,
    shipmentDate,
    shipperId,
    opportunityId,
    destinationWarehouseId,
    inputsKey,
  ]);

  // Comparaciones de precio (antes de los early-returns para sincronizar el flag del contexto).
  const enteredFob = Number(fobPriceUsdLb?.value || 0);
  const enteredSellerRaw = Number(priceUsdLb?.value || 0);
  const enteredSeller = enteredSellerRaw > 0 ? enteredSellerRaw : enteredFob;
  const fobBelow = status === 'ok' && fobCalc != null && enteredFob < fobCalc;
  const incotermBelow =
    status === 'ok' && incotermCalc != null && enteredSeller < incotermCalc;
  const belowPrice = fobBelow || incotermBelow;

  useEffect(() => {
    setPriceBelowSimulator?.(incotermBelow);
    return (): void => setPriceBelowSimulator?.(false);
  }, [incotermBelow, setPriceBelowSimulator]);

  useEffect(() => {
    setFobBelowSimulator?.(fobBelow);
    return (): void => setFobBelowSimulator?.(false);
  }, [fobBelow, setFobBelowSimulator]);

  // PLA-1057: bloqueo client-side. Si hay prima negativa sin marca PEEBIES, intercepta el submit
  // del form (form nativo de Rails), evita la recarga, resalta el campo de marca y avisa. El
  // modelo valida igual en el backend (red de seguridad).
  useEffect(() => {
    const markContainer = (): HTMLElement | null => {
      const el = document.querySelector('[name="contract[mark_id]"]');
      return (
        ((el?.closest('.input-group') ?? el?.parentElement) as HTMLElement | null) ?? null
      );
    };

    if (!peebiesMissing) {
      markContainer()?.classList.remove('input-group--invalid');
      return undefined;
    }

    const onSubmit = (e: Event): void => {
      const form = e.target as HTMLFormElement;
      // Solo el form de contrato (tiene el hidden de negative_premium que renderizamos).
      if (!form?.querySelector?.('[name="contract[pricing_inputs][negative_premium]"]'))
        return;

      e.preventDefault();
      e.stopPropagation();
      const container = markContainer();
      container?.classList.add('input-group--invalid');
      container?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.alert(t('peebies_required'));
    };

    document.addEventListener('submit', onSubmit, true);
    return (): void => document.removeEventListener('submit', onSubmit, true);
  }, [peebiesMissing]);

  const note = (message: string): JSX.Element => (
    <FieldSetWithTitle title={t('title')}>
      <p className='price-simulator__note'>{message}</p>
    </FieldSetWithTitle>
  );

  if (!isSupportedIncoterm) return note(t('not_applicable', { incoterm }));
  if (facilityMissing) return note(t('facility_not_configured'));
  if (unitsMissing) return note(t('units_required'));
  if (warehouseMissing) return note(t('warehouse_required'));

  const loading = status === 'loading';
  const format = (value: number | null): string =>
    value != null ? value.toFixed(4) : '';

  const priceMessage = (): string | null => {
    if (loading) return t('calculating');
    if (hasAdditionalFields && additionalFieldsMissing)
      return t('additional_fields_required');
    if (firstFieldBelowMin)
      return t('field_below_min', {
        field: localize(firstFieldBelowMin.label, firstFieldBelowMin.name),
        min: firstFieldBelowMin.min,
      });
    if (firstCostBelowMin)
      return t('field_below_min', {
        field: firstCostBelowMin.name,
        min: firstCostBelowMin.min,
      });
    if (status === 'unsupported') {
      if (reason === 'units') return t('units_required');
      if (reason === 'warehouse') return t('warehouse_required');
      // PLA-1111: mismos motivos que bloquean en el servidor
      // (Contracts::PricingSimulator::CreationCheckService::BLOCKING_REASONS).
      if (blocksOnMissingPriceData) {
        if (reason === 'quality') return t('quality_blocked', { quality: qualityName });
        if (reason === 'country') return t('country_blocked');
        if (reason === 'invalid_value' && apiMessage)
          return t('invalid_value_blocked', { message: apiMessage });
        if (reason === 'api_message' && apiMessage)
          return t('creation_blocked', { message: apiMessage });
      }
      if (reason === 'quality')
        return t('quality_not_configured', { quality: qualityName });
      if ((reason === 'api_message' || reason === 'invalid_value') && apiMessage)
        return t('api_message', { message: apiMessage });
      if (reason === 'calculation') return t('simulator_error');
      return t('cannot_calculate');
    }
    return null;
  };
  const message = priceMessage();

  // PLA-1057: los Additional fields (primas / custom margin) son parte del cálculo. Si el simulador
  // no puede calcular (status 'unsupported': p. ej. sin base data para país/calidad/volumen/incoterm
  // -> aprobación manual) no deben mostrarse, porque no afectan nada ni se guardan. Excepción: cuando
  // el no-cálculo es justamente porque faltan esos campos por llenar, sí deben verse para llenarlos.
  // PLA-1102: si el API rechazó un VALOR (`invalid_value`, un 4xx que no es 422), los campos
  // siguen a la vista: el mensaje suele nombrar uno ("customer transportation must be greater
  // than 0") y ocultarlos dejaba al usuario sin forma de corregirlo. Ante falta de configuración
  // (`api_message`, el 422: "No base data is configured…") o un error del servidor se ocultan,
  // porque desde el formulario no se arregla.
  const valuesRejected = status === 'unsupported' && reason === 'invalid_value';
  const showAdditionalFields =
    hasAdditionalFields &&
    (status !== 'unsupported' || additionalFieldsMissing || valuesRejected);

  const setFieldValue = (name: string, value: string): void =>
    setFieldValues((prev) => ({ ...prev, [name]: value }));

  const renderField = (field: SimulatorField): JSX.Element | null => {
    const label = localize(field.label, field.name);
    const inputName = `contract[pricing_inputs][${toSnake(field.name)}]`;

    if (field.type === 'multiselect') {
      return (
        <div key={field.name} className='input-group'>
          <label>
            {label}
            {field.required ? '*' : ''}
          </label>
          <MultiSelect
            name='contract[pricing_inputs][premium_ids]'
            placeholder={t('select_premiums')}
            options={premiums.map((p) => ({ value: p.id, label: p.label.trim() }))}
            value={premiums
              .filter((p) => premiumIds.includes(p.id))
              .map((p) => ({ value: p.id, label: p.label.trim() }))}
            onChange={(selected) => setPremiumIds(selected.map((s) => s.value))}
          />
        </div>
      );
    }

    if (field.type === 'select') {
      const options = field.optionsFrom === 'boxes' ? boxes : [];
      return (
        <div key={field.name} className='input-group'>
          <label>
            {label}
            {field.required ? '*' : ''}
          </label>
          <select
            name={inputName}
            value={fieldValues[field.name] ?? ''}
            onChange={(e) => setFieldValue(field.name, e.target.value)}
          >
            <option value=''>—</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    // number | date
    return (
      <InputGroupComponent
        key={field.name}
        label={label}
        type={field.type === 'date' ? 'date' : 'number'}
        step={field.step != null ? String(field.step) : '0.0001'}
        min={field.min != null ? String(field.min) : undefined}
        required={field.required}
        name={inputName}
        value={fieldValues[field.name] ?? ''}
        onChange={(e) => setFieldValue(field.name, e.target.value)}
      />
    );
  };

  return (
    <>
      {showAdditionalFields && (
        <FieldSetWithTitle title={t('additional_fields_title')}>
          <Grid>
            {visibleFields.map(renderField)}
            {editableCosts.map((cost) => (
              <InputGroupComponent
                key={cost.ddp_cost_id}
                label={cost.name}
                type='number'
                step={cost.step != null ? String(cost.step) : '0.0001'}
                min={cost.min != null ? String(cost.min) : undefined}
                required
                name={`contract[pricing_ddp_overrides][${cost.ddp_cost_id}]`}
                value={ddpOverrides[cost.ddp_cost_id] ?? String(cost.value)}
                onChange={(e) => {
                  const { value } = e.target;
                  setDdpOverrides((prev) => ({ ...prev, [cost.ddp_cost_id]: value }));
                }}
              />
            ))}
          </Grid>

          {/* Bandera para la validación PEEBIES en el backend (prima negativa -> marca PEEBIES). */}
          <input
            type='hidden'
            name='contract[pricing_inputs][negative_premium]'
            value={negativePremiumSelected ? 'true' : 'false'}
          />
          {peebiesMissing && (
            <p className='price-simulator__warning'>{t('peebies_required')}</p>
          )}
        </FieldSetWithTitle>
      )}

      <FieldSetWithTitle title={t('title')}>
        {message ? (
          <p className='price-simulator__note'>{message}</p>
        ) : (
          <>
            <Grid>
              <div>
                <InputGroupComponent
                  label={t('fob')}
                  append='USD/lb'
                  readOnly
                  disabled
                  value={format(fobCalc)}
                />
                {fobBelow && (
                  <span className='price-simulator__warning'>
                    {t('below_calculated')}
                  </span>
                )}
              </div>
              <div className='price-simulator__field'>
                <InputGroupComponent
                  label={t('simulator')}
                  append='USD/lb'
                  readOnly
                  disabled
                  value={format(incotermCalc)}
                />
                {breakdown && (
                  <span className='price-simulator__info'>
                    <PriceBreakdownTooltip breakdown={breakdown} />
                  </span>
                )}
                {incotermBelow && (
                  <span className='price-simulator__warning'>
                    {t('below_calculated')}
                  </span>
                )}
              </div>
            </Grid>

            {/* Snapshot de los precios calculados por el simulador (columnas dedicadas) para
                mostrarlos en la aprobación, sin recalcular (evita drift). */}
            <input
              type='hidden'
              name='contract[price_simulator_value]'
              value={incotermCalc != null ? String(incotermCalc) : ''}
            />
            <input
              type='hidden'
              name='contract[price_simulator_fob]'
              value={fobCalc != null ? String(fobCalc) : ''}
            />
            {/* PLA-1092: el desglose (fórmula + componentes) se congela junto a los precios para
                poder mostrar el mismo tooltip en la aprobación, sin volver a llamar al API. */}
            <input
              type='hidden'
              name='contract[price_simulator_breakdown]'
              value={breakdown ? JSON.stringify(breakdown) : ''}
            />

            {belowPrice && (
              <>
                <div className='input-group price-simulator__reason'>
                  <label htmlFor='contract_price_difference_reason'>
                    {t('difference_reason')}*
                  </label>
                  <textarea
                    id='contract_price_difference_reason'
                    name='contract[price_difference_reason]'
                    required
                  />
                </div>
                <div className='input-group price-simulator__document'>
                  <label htmlFor='contract_price_difference_document'>
                    {t('difference_document')}
                  </label>
                  <input
                    id='contract_price_difference_document'
                    type='file'
                    name='contract[price_difference_document]'
                    accept='.pdf,.xlsx,.docx,.pptx,.jpg,.jpeg,.png'
                  />
                </div>
              </>
            )}
          </>
        )}
      </FieldSetWithTitle>
    </>
  );
}

export default PriceSimulator;
